import * as ProgressPrimitive from '@radix-ui/react-progress';
import { resolveStatus, type StatusEnum, type StatusTone } from '@saay/formatting';
import { type ReactNode, useCallback } from 'react';

import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';
import type { MaskedKind } from '@saay/formatting';
import { IconButton, Ltr, Masked } from './primitives.js';
import { useToast } from './toast.js';
import type { DetailItem, TimelineItem } from './display.js';
import { Timeline } from './display.js';

const TONE_ICON: Record<StatusTone, IconName> = {
  danger: 'close',
  info: 'info',
  neutral: 'more',
  success: 'check',
  warning: 'alert',
};

/** The tones a status chip takes: the dictionary tones, plus `on-card` on the brand credit card. */
export type StatusChipTone = StatusTone | 'on-card';

/**
 * The one status component of every app (owner W4 decision: the status badge and the status chip
 * merged). A dictionary status (`enumName` + `value`) brings its label and tone; a custom label
 * (`title`) is neutral unless `tone` says otherwise. A tone always comes with a glyph (never color
 * alone); a neutral chip carries none, since a «more» glyph next to a label reads as a menu.
 */
export function StatusChip({
  className,
  enumName,
  icon,
  meta,
  title,
  tone: toneOverride,
  value,
}: {
  readonly className?: string;
  readonly enumName?: StatusEnum;
  /** A glyph other than the tone's own (for example a clock for «در انتظار تأیید»). */
  readonly icon?: IconName;
  readonly meta?: ReactNode;
  readonly title?: ReactNode;
  readonly tone?: StatusChipTone;
  readonly value?: string;
}) {
  const resolved =
    enumName !== undefined && value !== undefined
      ? resolveStatus(enumName, value)
      : {
          known: true,
          labelFa: title ?? '',
          tone: 'neutral' as StatusTone,
        };
  const label = title ?? resolved.labelFa;
  const tone: StatusChipTone = toneOverride ?? (title === undefined ? resolved.tone : 'neutral');
  const glyph = icon ?? (tone === 'neutral' || tone === 'on-card' ? undefined : TONE_ICON[tone]);
  return (
    <span
      className={className === undefined ? 'saay-status-chip' : `saay-status-chip ${className}`}
      data-known={enumName === undefined ? true : resolved.known}
      data-tone={tone}
      title={meta === undefined ? undefined : String(meta)}
    >
      {glyph === undefined ? null : <Icon name={glyph} />}
      <span>{label}</span>
      {meta === undefined ? null : <span className="saay-status-chip__meta">{meta}</span>}
    </span>
  );
}

/** Determinate or indeterminate linear progress (F3). */
export function LinearProgress({
  indeterminate = false,
  label,
  value,
}: {
  readonly indeterminate?: boolean;
  readonly label: string;
  readonly value?: number;
}) {
  const percent = value === undefined ? 0 : Math.min(100, Math.max(0, value));
  return (
    <ProgressPrimitive.Root
      aria-label={label}
      className="saay-progress-linear"
      data-indeterminate={indeterminate || undefined}
      value={indeterminate ? null : percent}
    >
      <ProgressPrimitive.Indicator
        className="saay-progress-linear__bar"
        style={indeterminate ? undefined : { inlineSize: `${percent}%` }}
      />
    </ProgressPrimitive.Root>
  );
}

/** Circular loading indicator (F3). */
export function CircularProgress({ label }: { readonly label: string }) {
  return <div aria-label={label} className="saay-progress-circular" role="progressbar" />;
}

export type SkeletonVariant = 'lines' | 'table' | 'list' | 'tile' | 'card' | 'detail';

/** Layout-shaped skeleton placeholders (F4). */
export function SkeletonShape({
  label = 'در حال بارگذاری…',
  lines = 3,
  rows = 4,
  variant = 'lines',
}: {
  readonly label?: string;
  readonly lines?: number;
  readonly rows?: number;
  readonly variant?: SkeletonVariant;
}) {
  if (variant === 'lines') {
    return (
      <div aria-busy="true" aria-live="polite" className="saay-skeleton" role="status">
        <span className="saay-visually-hidden">{label}</span>
        {Array.from({ length: lines }, (_, index) => (
          <span
            aria-hidden="true"
            className="saay-skeleton__line"
            key={index}
            style={{ inlineSize: `${index === lines - 1 ? 60 : 100}%` }}
          />
        ))}
      </div>
    );
  }
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className="saay-skeleton"
      data-variant={variant}
      role="status"
    >
      <span className="saay-visually-hidden">{label}</span>
      {variant === 'table'
        ? Array.from({ length: rows }, (_, row) => (
            <div aria-hidden="true" className="saay-skeleton__row" key={row}>
              {Array.from({ length: 4 }, (_, cell) => (
                <span className="saay-skeleton__cell" key={cell} />
              ))}
            </div>
          ))
        : null}
      {variant === 'list'
        ? Array.from({ length: rows }, (_, row) => (
            <div aria-hidden="true" className="saay-skeleton__row" key={row}>
              <span className="saay-skeleton__avatar saay-skeleton__block" />
              <span className="saay-skeleton__cell" style={{ flex: 1 }} />
            </div>
          ))
        : null}
      {variant === 'tile'
        ? Array.from({ length: rows }, (_, tile) => (
            <span aria-hidden="true" className="saay-skeleton__block" key={tile} />
          ))
        : null}
      {variant === 'card' ? <span aria-hidden="true" className="saay-skeleton__block" /> : null}
      {variant === 'detail'
        ? Array.from({ length: rows }, (_, row) => (
            <div aria-hidden="true" className="saay-skeleton__row" key={row}>
              <span className="saay-skeleton__cell" />
              <span className="saay-skeleton__cell" />
            </div>
          ))
        : null}
    </div>
  );
}

export type KeyValueItem = DetailItem & {
  readonly copyable?: boolean;
  readonly masked?: MaskedKind;
};

/** Key–value detail list (V2) with optional copy on identifiers. */
export function KeyValueList({
  items,
  label,
  onCopy,
}: {
  readonly items: readonly KeyValueItem[];
  readonly label?: string;
  readonly onCopy?: (value: string) => void;
}) {
  const toast = useToast();
  const copy = useCallback(
    (raw: string) => {
      void navigator.clipboard?.writeText(raw).then(() => {
        onCopy?.(raw);
        toast?.notify({ title: 'کپی شد' });
      });
    },
    [onCopy, toast],
  );
  return (
    <dl {...(label === undefined ? {} : { 'aria-label': label })} className="saay-kv-list">
      {items.map((item) => {
        const raw = typeof item.value === 'string' ? item.value : null;
        const showCopy = item.copyable === true && raw !== null && item.masked === undefined;
        return (
          <div className="saay-kv-list__row" key={item.key}>
            <dt className="saay-kv-list__label">{item.label}</dt>
            <dd className="saay-kv-list__value">
              {item.value === null || item.value === undefined || item.value === '' ? (
                <span className="saay-kv-list__empty">—</span>
              ) : item.masked !== undefined && typeof item.value === 'string' ? (
                <Masked kind={item.masked} value={item.value} />
              ) : (
                item.value
              )}
              {showCopy ? (
                <IconButton icon="copy" label="کپی" onClick={() => copy(raw)} variant="standard" />
              ) : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export type DiffRow = {
  readonly changed?: boolean;
  readonly key: string;
  readonly label: ReactNode;
  readonly next?: ReactNode;
  readonly previous?: ReactNode;
};

/** Before/after diff rows (V3). */
export function DiffList({
  items,
  label,
}: {
  readonly items: readonly DiffRow[];
  readonly label: string;
}) {
  return (
    <div aria-label={label} className="saay-diff-list" role="list">
      {items.map((item) => (
        <div
          className="saay-diff-list__row"
          data-changed={item.changed === true || undefined}
          key={item.key}
          role="listitem"
        >
          <div className="saay-kv-list__label">{item.label}</div>
          {item.changed ? (
            <div className="saay-diff-list__values">
              <span>{item.previous ?? '—'}</span>
              <span aria-hidden="true" className="saay-diff-list__arrow">
                →
              </span>
              <span>{item.next ?? '—'}</span>
            </div>
          ) : (
            <div>{item.next ?? item.previous ?? '—'}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export type TimelineGroup = {
  readonly heading: ReactNode;
  readonly items: readonly TimelineItem[];
  readonly key: string;
};

/** Timeline grouped by day headings (V6). */
export function GroupedTimeline({
  groups,
  label,
}: {
  readonly groups: readonly TimelineGroup[];
  readonly label: string;
}) {
  return (
    <div aria-label={label}>
      {groups.map((group) => (
        <section key={group.key}>
          <h3 className="saay-timeline-group__heading">{group.heading}</h3>
          <Timeline items={group.items} label={`${label}: ${String(group.heading)}`} />
        </section>
      ))}
    </div>
  );
}

export type InstallmentLine = {
  readonly amount: ReactNode;
  readonly extra?: ReactNode;
  readonly key: string;
  readonly label: ReactNode;
  readonly status?: 'default' | 'due' | 'overdue';
  readonly statusChip?: ReactNode;
  readonly title: ReactNode;
};

/** Installment schedule with due/overdue emphasis (V9). */
export function InstallmentSchedule({
  items,
  label,
}: {
  readonly items: readonly InstallmentLine[];
  readonly label: string;
}) {
  return (
    <ol aria-label={label} className="saay-schedule-strip saay-installment-schedule">
      {items.map((item) => (
        <li
          className="saay-schedule-step"
          data-status={item.status === 'default' ? undefined : item.status}
          key={item.key}
        >
          <span>{item.label}</span>
          <strong>{item.title}</strong>
          {item.statusChip ?? null}
          <small>{item.amount}</small>
          {item.extra === undefined ? null : (
            <span className="saay-schedule-step__extra">{item.extra}</span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Copyable identifier (V11): snackbar on copy, no label swap; masked values cannot be copied. */
export function CopyableIdentifier({
  copyLabel = 'کپی',
  label,
  masked,
  value,
}: {
  readonly copyLabel?: string;
  readonly label: ReactNode;
  readonly masked?: MaskedKind;
  readonly value: string;
}) {
  const toast = useToast();
  const canCopy = masked === undefined;
  return (
    <span className="saay-copyable-id">
      <span>{label}</span>
      <span className="saay-copyable-id__value">
        {masked === undefined ? <Ltr>{value}</Ltr> : <Masked kind={masked} value={value} />}
      </span>
      {canCopy ? (
        <IconButton
          icon="copy"
          label={copyLabel}
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              toast?.notify({ title: 'کپی شد' });
            });
          }}
          variant="standard"
        />
      ) : null}
    </span>
  );
}
